// ---------------------------------------------------------------------------
// The pacing sheet → progression data
// ---------------------------------------------------------------------------
//
// The progression numbers are designed in a spreadsheet and exported as the
// CSVs in ./pacing/. importPacing turns their text into the plain data object
// that ./pacing.js holds (scripts/import-pacing.mjs writes it). It reads no
// files, so the parity test can run it on the CSVs and compare.
//
// Every check fails loudly: a typo in the sheet must never quietly become a
// default. Retuning is: edit the prototype config sheet (one tab per CSV),
// write its tabs over ./pacing/ (scripts/pacing-from-sheet.mjs), run
// `npm run import-pacing`, re-run the sim.

export const PACING_FILES = ['arenas', 'levels', 'structures', 'lot_types', 'chest_payload', 'genesis_chest', 'inputs']

// Raw resources by name: every lot type, plus resources only chests carry.
export const RAW_RESOURCES = ['timber', 'metal', 'coal', 'water', 'oil', 'silicon', 'sand', 'limestone']
export const REFINED_RESOURCES = ['Steel', 'Concrete']

// Input dials: CSV name → key and type. A row the sheet adds that isn't here is
// refused, and so is a missing one.
const INPUTS = {
    yield_per_lot_per_round:                 ['yieldPerLotPerRound', 'number'],
    extractor_yield_multiplier:              ['extractorYieldMultiplier', 'number'],
    survival_reward_bucks_per_structure_level: ['survivalRewardBucksPerLevel', 'number'],
    loser_survival_reward_share:             ['loserSurvivalRewardShare', 'number'],
    war_chests_per_win:                      ['warChestsPerWin', 'number'],
    gem_price_per_round_remaining:           ['gemPricePerRoundRemaining', 'number'],
    max_structures_hard_cap:                 ['maxStructuresHardCap', 'number'],
    hive_cells:                              ['hiveCells', 'number'],
    async_turn_deadline_hours:               ['asyncTurnDeadlineHours', 'number'],
    async_consecutive_misses_to_forfeit:     ['asyncMissesToForfeit', 'number'],
    converter_refined_output_per_war:        ['converterRefinedOutputPerWar', 'number'],
    converter_raw_input_per_refined_unit:    ['converterRawInputPerRefinedUnit', 'number'],
    yield_to_raw_rate:                       ['yieldToRawRate', 'number'],
    lot_overflow_raw:                        ['lotOverflowRaw', 'number'],
    unowned_build_weight:                    ['unownedBuildWeight', 'number'],
    sync_clock_seconds:                      ['syncClockSeconds', 'number'],
    sync_reveal_allowance_seconds:           ['syncRevealAllowanceSeconds', 'number'],
    invite_expiry_hours:                     ['inviteExpiryHours', 'number'],
    converters_enabled:                      ['convertersEnabled', 'boolean'],
    rocket_cost_yield:                       ['rocketCostYield', 'number'],
    recon_cost_yield:                        ['reconCostYield', 'number'],
}

// ── CSV ─────────────────────────────────────────────────────────────────────

// RFC 4180-ish: quoted fields may hold commas, quotes ("") and newlines.
export function parseCsv(text, file = 'csv') {
    const rows = []
    let row = [], cell = '', quoted = false
    const src = text.replace(/^﻿/, '')
    for (let i = 0; i < src.length; i++) {
        const c = src[i]
        if (quoted) {
            if (c === '"' && src[i + 1] === '"') { cell += '"'; i++ }
            else if (c === '"') quoted = false
            else cell += c
        } else if (c === '"') quoted = true
        else if (c === ',') { row.push(cell); cell = '' }
        else if (c === '\n' || c === '\r') {
            if (c === '\r' && src[i + 1] === '\n') i++
            row.push(cell); rows.push(row); row = []; cell = ''
        } else cell += c
    }
    if (cell !== '' || row.length) { row.push(cell); rows.push(row) }
    const lines = rows.filter(r => r.some(v => v.trim() !== ''))
    if (!lines.length) throw new Error(`${file}: empty`)
    const head = lines[0].map(h => h.trim())
    return lines.slice(1).map((r, n) => {
        if (r.length !== head.length) throw new Error(`${file} row ${n + 2}: ${r.length} cells, header has ${head.length}`)
        return Object.fromEntries(head.map((h, i) => [h, r[i].trim()]))
    })
}

function requireColumns(rows, cols, file) {
    const have = new Set(Object.keys(rows[0] ?? {}))
    const missing = cols.filter(c => !have.has(c))
    const extra = [...have].filter(c => !cols.includes(c))
    if (missing.length) throw new Error(`${file}: missing column(s) ${missing.join(', ')}`)
    if (extra.length) throw new Error(`${file}: unknown column(s) ${extra.join(', ')}`)
}

const num = (v, where) => {
    if (v === '' || !Number.isFinite(Number(v))) throw new Error(`${where}: "${v}" is not a number`)
    return Number(v)
}
const int = (v, where) => {
    const n = num(v, where)
    if (!Number.isInteger(n)) throw new Error(`${where}: "${v}" is not a whole number`)
    return n
}
const bool = (v, where) => {
    if (v === 'true' || v === 'TRUE') return true
    if (v === 'false' || v === 'FALSE') return false
    throw new Error(`${where}: "${v}" is not true or false`)
}
const raw = (v, where) => {
    if (!RAW_RESOURCES.includes(v)) throw new Error(`${where}: unknown raw resource "${v}"`)
    return v
}
const orNone = v => (v === '' || v === 'none' ? null : v)

// "dq dr;dq dr;…" — a shape's hexes as axial offsets from its anchor hex.
export function parseShape(text, where) {
    const cells = text.split(';').map(s => s.trim()).filter(Boolean).map(pair => {
        const parts = pair.split(/\s+/)
        if (parts.length !== 2) throw new Error(`${where}: shape cell "${pair}" must be "dq dr"`)
        return parts.map(p => int(p, `${where} shape`))
    })
    if (!cells.length) throw new Error(`${where}: empty shape`)
    const keys = cells.map(c => c.join(','))
    if (new Set(keys).size !== keys.length) throw new Error(`${where}: shape repeats a hex`)
    if (!keys.includes('0,0')) throw new Error(`${where}: shape must include its anchor hex "0 0"`)
    return cells
}

// ── The sheets ──────────────────────────────────────────────────────────────

function arenasFrom(rows) {
    const f = 'arenas.csv'
    requireColumns(rows, ['arena', 'max_structures_on_hive', 'hive_level_to_ascend_into', 'max_structure_level', 'kills_to_win',
        'expected_wars_in_arena', 'cumulative_wars_to_reach', 'bot_structure_level', 'bot_lot_count'], f)
    const out = {}
    rows.forEach((r, i) => {
        const at = `${f} arena ${r.arena}`
        const a = int(r.arena, at)
        if (a !== i + 1) throw new Error(`${f}: arenas must run 1, 2, 3… in order (row ${i + 2} is ${r.arena})`)
        out[a] = {
            maxStructures: int(r.max_structures_on_hive, at),
            hiveLevelToAscendInto: int(r.hive_level_to_ascend_into, at),
            levelCap: int(r.max_structure_level, at),
            killsToWin: int(r.kills_to_win, at),
            expectedWars: num(r.expected_wars_in_arena, at),
            cumulativeWarsToReach: num(r.cumulative_wars_to_reach, at),
            botStructureLevel: int(r.bot_structure_level, at),
            botLotCount: int(r.bot_lot_count, at),
        }
    })
    return out
}

function levelsFrom(rows) {
    const f = 'levels.csv'
    requireColumns(rows, ['from_level', 'to_level', 'raw_cost_standard_tier', 'refined_units', 'duration_war_rounds',
        'gem_price_from_zero', 'bucks_cost_standard_tier'], f)
    const out = {}
    rows.forEach((r, i) => {
        const at = `${f} row ${i + 2}`
        const from = int(r.from_level, at), to = int(r.to_level, at)
        if (from !== i + 1 || to !== from + 1) throw new Error(`${at}: levels must run 1→2, 2→3… in order`)
        // Keyed by the level an upgrade reaches; structures start at level 1.
        out[to] = {
            rawCost: num(r.raw_cost_standard_tier, at),
            refinedUnits: int(r.refined_units, at),
            rounds: int(r.duration_war_rounds, at),
            gemPriceFromZero: num(r.gem_price_from_zero, at),
            bucksCost: num(r.bucks_cost_standard_tier, at),
        }
    })
    return out
}

function structuresFrom(rows) {
    const f = 'structures.csv'
    requireColumns(rows, ['structure', 'family', 'unlock_arena', 'upgrade_resource_a', 'upgrade_amount_a', 'upgrade_resource_b',
        'upgrade_amount_b', 'cost_tier_multiplier', 'refined_input_from_L6', 'is_extractor_for_lot_type',
        'duplicate_payout_resource_a', 'duplicate_payout_amount_a', 'duplicate_payout_resource_b', 'duplicate_payout_amount_b',
        'stops_lot_production', 'shape', 'converter_output', 'notes'], f)
    const out = {}
    for (const r of rows) {
        const at = `${f} ${r.structure || '(unnamed)'}`
        if (!r.structure) throw new Error(`${f}: a row has no structure name`)
        if (out[r.structure]) throw new Error(`${f}: ${r.structure} is listed twice`)
        const pair = (ra, aa, rb, ab, what) => {
            const a = raw(ra, `${at} ${what} a`), b = raw(rb, `${at} ${what} b`)
            if (a === b) throw new Error(`${at}: ${what} names ${a} twice`)
            return { [a]: num(aa, `${at} ${what} a`), [b]: num(ab, `${at} ${what} b`) }
        }
        const refinedInput = orNone(r.refined_input_from_L6)
        if (refinedInput && !REFINED_RESOURCES.includes(refinedInput)) throw new Error(`${at}: unknown refined resource "${refinedInput}"`)
        const converterOutput = orNone(r.converter_output)
        if (converterOutput && !REFINED_RESOURCES.includes(converterOutput)) throw new Error(`${at}: unknown refined resource "${converterOutput}"`)
        out[r.structure] = {
            family: r.family,
            unlockArena: int(r.unlock_arena, at),
            recipe: pair(r.upgrade_resource_a, r.upgrade_amount_a, r.upgrade_resource_b, r.upgrade_amount_b, 'upgrade recipe'),
            costTier: num(r.cost_tier_multiplier, at),
            refinedInput,
            extractorFor: orNone(r.is_extractor_for_lot_type),
            duplicatePayout: pair(r.duplicate_payout_resource_a, r.duplicate_payout_amount_a,
                r.duplicate_payout_resource_b, r.duplicate_payout_amount_b, 'duplicate payout'),
            stopsLotProduction: bool(r.stops_lot_production, at),
            shape: parseShape(r.shape, at),
            // A converter refines its upgrade recipe's two raw resources into this.
            converterOutput,
            notes: r.notes,
        }
    }
    return out
}

function lotTypesFrom(rows) {
    const f = 'lot_types.csv'
    requireColumns(rows, ['lot_type', 'paired_extractor', 'first_drops_in_arena'], f)
    const out = {}
    for (const r of rows) {
        const at = `${f} ${r.lot_type}`
        out[raw(r.lot_type, at)] = { extractor: r.paired_extractor, firstArena: int(r.first_drops_in_arena, at) }
    }
    return out
}

function chestsFrom(rows) {
    const f = 'chest_payload.csv'
    const rawCols = RAW_RESOURCES.map(res => `${res}_per_chest`)
    requireColumns(rows, ['arena', ...rawCols, 'lot_cards_per_chest', 'gems_per_chest'], f)
    const out = {}
    for (const r of rows) {
        const at = `${f} arena ${r.arena}`
        out[int(r.arena, at)] = {
            raw: Object.fromEntries(RAW_RESOURCES.map(res => [res, num(r[`${res}_per_chest`], at)])),
            lotCards: int(r.lot_cards_per_chest, at),
            gems: num(r.gems_per_chest, at),
        }
    }
    return out
}

function genesisFrom(rows) {
    const f = 'genesis_chest.csv'
    requireColumns(rows, ['item', 'type', 'count', 'notes'], f)
    const out = { structures: [], lots: {} }
    for (const r of rows) {
        const at = `${f} ${r.item} ${r.type}`
        const n = int(r.count, at)
        if (r.item === 'structure') {
            if (n !== 1) throw new Error(`${at}: a structure type can only be owned once`)
            out.structures.push(r.type)
        } else if (r.item === 'lot') out.lots[raw(r.type, at)] = (out.lots[r.type] ?? 0) + n
        else throw new Error(`${at}: item must be "structure" or "lot"`)
    }
    return out
}

function inputsFrom(rows) {
    const f = 'inputs.csv'
    requireColumns(rows, ['dial', 'value', 'notes'], f)
    const out = {}
    for (const r of rows) {
        const spec = INPUTS[r.dial]
        if (!spec) throw new Error(`${f}: unknown dial "${r.dial}"`)
        const [key, type] = spec
        if (key in out) throw new Error(`${f}: ${r.dial} is listed twice`)
        out[key] = type === 'boolean' ? bool(r.value, `${f} ${r.dial}`) : num(r.value, `${f} ${r.dial}`)
    }
    const missing = Object.keys(INPUTS).filter(d => !(INPUTS[d][0] in out))
    if (missing.length) throw new Error(`${f}: missing dial(s) ${missing.join(', ')}`)
    return out
}

// ── Cross-checks ────────────────────────────────────────────────────────────

export function checkPacing(p) {
    const arenaNums = Object.keys(p.arenas).map(Number)
    const top = Math.max(...arenaNums)
    const isArena = (a, where) => { if (!p.arenas[a]) throw new Error(`${where}: arena ${a} is not in arenas.csv`) }
    const topLevel = Math.max(1, ...Object.keys(p.levels).map(Number))

    for (const a of arenaNums) {
        if (!p.chests[a]) throw new Error(`chest_payload.csv: no row for arena ${a}`)
        const A = p.arenas[a]
        if (A.levelCap > topLevel) throw new Error(`arenas.csv arena ${a}: level cap ${A.levelCap} is above levels.csv's top level ${topLevel}`)
        if (A.botStructureLevel > A.levelCap) throw new Error(`arenas.csv arena ${a}: bot level is above the level cap`)
        if (A.maxStructures > p.inputs.maxStructuresHardCap) throw new Error(`arenas.csv arena ${a}: max structures is above max_structures_hard_cap`)
        if (a > 1 && A.hiveLevelToAscendInto < p.arenas[a - 1].hiveLevelToAscendInto) throw new Error(`arenas.csv arena ${a}: Hive Level to ascend goes down`)
    }
    for (const a of Object.keys(p.chests)) isArena(Number(a), `chest_payload.csv arena ${a}`)

    for (const [name, s] of Object.entries(p.structures)) {
        isArena(s.unlockArena, `structures.csv ${name}`)
        if (s.extractorFor) {
            const lot = p.lotTypes[s.extractorFor]
            if (!lot) throw new Error(`structures.csv ${name}: extracts unknown lot type "${s.extractorFor}"`)
            if (lot.extractor !== name) throw new Error(`structures.csv ${name}: lot_types.csv pairs ${s.extractorFor} with ${lot.extractor}`)
        }
        if (s.converterOutput && s.extractorFor) throw new Error(`structures.csv ${name}: can't be both an extractor and a converter`)
    }
    for (const [lot, L] of Object.entries(p.lotTypes)) {
        isArena(L.firstArena, `lot_types.csv ${lot}`)
        if (p.structures[L.extractor]?.extractorFor !== lot) throw new Error(`lot_types.csv ${lot}: ${L.extractor} isn't marked as its extractor in structures.csv`)
    }
    for (const name of p.genesisChest.structures) {
        if (!p.structures[name]) throw new Error(`genesis_chest.csv: unknown structure "${name}"`)
    }
    for (const lot of Object.keys(p.genesisChest.lots)) {
        if (!p.lotTypes[lot]) throw new Error(`genesis_chest.csv: unknown lot type "${lot}"`)
    }
    if (!(top >= 1)) throw new Error('arenas.csv: no arenas')
    return p
}

// texts: { arenas: '<csv text>', levels: …, … } (keys as PACING_FILES).
export function importPacing(texts) {
    for (const f of PACING_FILES) if (typeof texts[f] !== 'string') throw new Error(`missing ${f}.csv`)
    const rows = Object.fromEntries(PACING_FILES.map(f => [f, parseCsv(texts[f], `${f}.csv`)]))
    return checkPacing({
        arenas: arenasFrom(rows.arenas),
        levels: levelsFrom(rows.levels),
        structures: structuresFrom(rows.structures),
        lotTypes: lotTypesFrom(rows.lot_types),
        chests: chestsFrom(rows.chest_payload),
        genesisChest: genesisFrom(rows.genesis_chest),
        inputs: inputsFrom(rows.inputs),
    })
}
